import type { UIMessage } from "../../types/chat";
import { Icon } from "../ui/Icon";
import { cn } from "../../lib/utils";
import DocumentTextLinear from "~icons/solar/document-text-linear";

interface UserMessageBubbleProps {
  message: UIMessage;
  initials: string;
  avatarUrl?: string;
}

/**
 * Display-only transform: replaces underscores with spaces inside @mention tokens.
 * e.g. "@Middle_Go_Lang_Developer" → "@Middle Go Lang Developer"
 * The stored/sent content is never modified — this only affects rendering.
 */
function formatMessageForDisplay(content: string): string {
  return content.replace(
    /@(\S+)/g,
    (_match, word: string) => `@${word.replaceAll("_", " ")}`
  );
}

export function UserMessageBubble({
  message,
  initials,
  avatarUrl,
}: UserMessageBubbleProps) {
  return (
    <div className="flex items-start justify-end gap-2 pl-6 lg:gap-3 lg:pl-[120px]">
      <div className="rounded-[20px_20px_4px_20px] lg:rounded-[24px_24px_4px_24px] bg-bubble-user px-3 py-2 lg:px-4 lg:py-3">
        {message.content && (
          <p className="whitespace-pre-wrap text-body-md font-normal text-text-primary">
            {formatMessageForDisplay(message.content)}
          </p>
        )}
        {(() => {
          const visibleDocuments = message.documents?.filter((d) => !d.silent);
          return visibleDocuments && visibleDocuments.length > 0 ? (
            <div
              className={cn("flex flex-col gap-1", message.content && "mt-2")}
            >
              {visibleDocuments.map((doc, index) => {
                const fileName =
                  doc.file_name ??
                  (typeof doc.document_url === "string"
                    ? doc.document_url.split("/").pop() || "Document"
                    : "Document");
                return (
                  <div
                    key={doc.document_url || index}
                    className="flex items-center gap-1.5 text-body-sm text-secondary"
                  >
                    <Icon
                      icon={DocumentTextLinear}
                      size="xs"
                      className="shrink-0"
                    />
                    <span className="truncate">{fileName}</span>
                  </div>
                );
              })}
            </div>
          ) : null;
        })()}
      </div>
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt="Your avatar"
          className="size-8 lg:size-10 shrink-0 rounded-full object-cover"
        />
      ) : (
        <div className="flex size-8 lg:size-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-gradient-start to-gradient-end">
          <span className="text-body-xs lg:text-body-md font-semibold text-white">
            {initials}
          </span>
        </div>
      )}
    </div>
  );
}
